import { useCallback, useRef, useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Button, Card, PageBody, SectionLabel } from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { ImportRowsTable } from '@/components/admin/bulk-import/ImportRowsTable.tsx';
import {
  ImportTargetCard,
  useImportTarget,
  type ImportTarget,
} from '@/components/admin/bulk-import/ImportTargetCard.tsx';
import { PasteArea } from '@/components/admin/bulk-import/PasteArea.tsx';
import {
  toImportRows,
  type ImportRow,
} from '@/components/admin/bulk-import/importRow.ts';
import { languageFields } from '@/utils/languageFields.ts';
import { parsePastedUsers } from '@/utils/parsePastedUsers.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@wundo/client/api.ts';
import { createUser } from '@/lib/users.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { reportError } from '@/lib/reportError.ts';

export const Route = createFileRoute('/_app/admin/bulk-import')({
  staticData: { orgScope: 'required' },
  component: BulkImportPage,
});

/**
 * Turning a pasted list of people into users on a cohort, in three steps on one
 * page. Nothing is created until the last button: a forgiving parser is only
 * safe if what it guessed is on screen and editable before anyone is provisioned.
 */
export function BulkImportPage() {
  const router = useRouter();
  const areaRef = useRef<HTMLDivElement | null>(null);

  const [pasted, setPasted] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [parseError, setParseError] = useState('');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const target = useImportTarget();
  const importRun = useImportRun(target, setRowState);
  const { organisation, cohort } = target;

  const editRow = useCallback(
    (key: string, field: 'firstName' | 'lastName' | 'email', value: string) => {
      setRows((current) =>
        current.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
      );
    },
    [],
  );

  const removeRow = useCallback((key: string) => {
    setRows((current) => current.filter((row) => row.key !== key));
  }, []);

  function setRowState(key: string, patch: Partial<ImportRow>) {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  }

  const onPasteChange = useCallback((markup: string, text: string) => {
    setPasted(markup);
    setPastedText(text);
  }, []);

  const parse = () => {
    setParseError('');
    setRows([]);

    if (!pasted.trim()) {
      setParseError('Please paste some data');
      return;
    }

    const parsed = parsePastedUsers(pasted);
    if (parsed.length === 0) {
      setParseError(
        'Could not parse any valid users. Please ensure your data includes email addresses.',
      );
      return;
    }

    setRows(toImportRows(parsed));
  };

  const reset = () => {
    setPasted('');
    setPastedText('');
    setRows([]);
    setParseError('');
    importRun.clearProgress();
    areaRef.current?.replaceChildren();
  };

  const startImport = () => {
    if (!organisation) {
      setParseError('Please select an organisation');
      return;
    }
    if (!cohort) {
      setParseError('Please select a cohort');
      return;
    }
    if (rows.length === 0) {
      setParseError('No users to import');
      return;
    }

    setParseError('');
    importRun.start(rows.filter((row) => row.status !== 'success'));
  };

  const { importing, progress } = importRun;
  const done = progress.success + progress.failed;

  return (
    <>
      <PageHeader title='Bulk import' />
      <PageBody className='flex flex-col gap-5.5'>
        {parseError !== '' && (
          <Card
            role='alert'
            tone='quiet'
            className='border-danger-line text-sm text-danger'
          >
            {parseError}
          </Card>
        )}

        <Card as='section' className='flex flex-col gap-4'>
          <SectionLabel as='h2'>Step 1: Paste user data</SectionLabel>
          <PasteInstructions />

          <PasteArea areaRef={areaRef} onChange={onPasteChange} />

          <div className='flex gap-3'>
            <Button onClick={parse} disabled={pastedText.trim() === ''}>
              Parse data
            </Button>
            {rows.length > 0 && (
              <Button variant='quiet' onClick={reset}>
                Reset
              </Button>
            )}
          </div>
        </Card>

        {rows.length > 0 && <ImportTargetCard target={target} />}

        {rows.length > 0 && (
          <Card as='section' className='flex flex-col gap-4'>
            <div className='flex items-center justify-between'>
              <SectionLabel as='h2'>Step 3: Review and edit users</SectionLabel>
              <span className='text-sm text-ink-muted'>{rows.length} user(s) found</span>
            </div>

            <ImportRowsTable
              rows={rows}
              importing={importing}
              onEdit={editRow}
              onRemove={removeRow}
            />

            {progress.total > 0 && (
              <p role='status' className='text-sm text-ink-secondary'>
                Import progress: {done} / {progress.total} — {progress.success}{' '}
                successful, {progress.failed} failed
              </p>
            )}

            <div className='flex gap-3'>
              <Button
                onClick={startImport}
                loading={importing}
                disabled={
                  importing ||
                  organisation === null ||
                  cohort === null ||
                  rows.length === 0
                }
              >
                {importing ? 'Importing...' : `Import ${rows.length} user(s)`}
              </Button>

              {!importing && progress.total > 0 && (
                <Button
                  variant='quiet'
                  onClick={() => void router.navigate({ to: '/users' })}
                >
                  View all users
                </Button>
              )}
            </div>
          </Card>
        )}
      </PageBody>
    </>
  );
}

interface ImportProgress {
  total: number;
  success: number;
  failed: number;
}

const NOTHING_YET: ImportProgress = { total: 0, success: 0, failed: 0 };

/**
 * Creating the reviewed rows as users, one person at a time: there is no bulk
 * endpoint, so a partial run is the normal outcome and a failed row must not
 * take the others down. The cohort goes with the user, so the organisation's
 * default is never joined.
 */
function useImportRun(
  target: ImportTarget,
  setRowState: (key: string, patch: Partial<ImportRow>) => void,
) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<ImportProgress>(NOTHING_YET);

  const runImport = useMutation({
    mutationFn: async (queue: ImportRow[]) => {
      setProgress({ total: queue.length, success: 0, failed: 0 });

      for (const row of queue) {
        setRowState(row.key, { status: 'creating', errorMessage: undefined });

        try {
          const created = await createUser({
            first_name: row.firstName,
            last_name: row.lastName || undefined,
            email: row.email,
            organisation_id: target.organisation!.id,
            channel: target.channel,
            notification_channel: target.notificationChannel,
            ...languageFields(target.language),
            cohort_id: target.cohort!.id,
          });

          setRowState(row.key, { status: 'success', userId: created.id });
          setProgress((current) => ({ ...current, success: current.success + 1 }));
        } catch (error) {
          if (!isRowRefusal(error)) {
            reportError(error, { where: 'bulkImport.row', email: row.email });
          }
          setRowState(row.key, { status: 'error', errorMessage: rowErrorMessage(error) });
          setProgress((current) => ({ ...current, failed: current.failed + 1 }));
        }
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  return {
    progress,
    clearProgress: () => setProgress(NOTHING_YET),
    importing: runImport.isPending,
    start: (queue: ImportRow[]) => runImport.mutate(queue),
  };
}

/**
 * Why one row did not become a user: the backend's own sentence where it sends
 * one, otherwise what the status means here. It sits on the row, because a run
 * of forty people that loses two has to say which two.
 */
function rowErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return apiErrorMessage(error, statusMessage(error));
  }
  return error instanceof Error ? error.message : 'Unknown error';
}

/*
 * A pasted list is someone else's data, so the API refusing one person in it
 * (an address that already has an account) is the row's outcome, written on
 * the row. Elsewhere a 422 means our own form sent a bad body.
 */
function isRowRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422;
}

function statusMessage(error: ApiError): string {
  if (error.status === 422) return 'Refused: check the name and the email address';
  if (error.status === 409) return 'This email address is already taken';
  if (error.status === 404) return 'The organisation or cohort no longer exists';
  return error.message;
}

/* The shapes the parser reads, so whoever pastes knows what will come out. */
function PasteInstructions() {
  return (
    <div className='text-sm text-ink-muted'>
      <p>
        Paste a list of users from Excel, Outlook or a CSV file. Include the header row
        (First name, Last name, Email) and the columns may come in any order.
      </p>
      <p>Without a header row:</p>
      <ul className='list-inside list-disc'>
        <li>Copied from a spreadsheet: Last name, First name, Email</li>
        <li>Typed text, one per line: First name Last name email@example.com</li>
      </ul>
    </div>
  );
}
